/**
 * Um botão que abre a lista das ações de um registro — o "Ações ⌄".
 *
 * Existe para juntar o que mexe no registro num lugar só, em vez de uma fileira de
 * botões no cabeçalho: na caixa de relés eram três ("incluir modelos", "excluir
 * modelo", "alterar posições") espalhados e disputando espaço com o título.
 *
 * O menu é o do Radix, como o "Mais" da barra de cima, e cresce com o A−/A+ (ver
 * `kit/flutuante.ts`). Ao escolher, o foco **não** volta para o botão: a ação quase
 * sempre abre um formulário ou uma confirmação com foco próprio, e o Radix devolvendo
 * o foco ao gatilho tirava o cursor do campo que acabou de abrir.
 */

import * as Menu from "@radix-ui/react-dropdown-menu"
import { escalaFlutuante } from "./flutuante"

export type AcaoDoMenu = {
  rotulo: string
  aoEscolher: () => void
  /** Uma linha miúda embaixo do rótulo, dizendo o que a ação faz. */
  dica?: string
  /** Ação que apaga: vai em vermelho e, se houver outras antes, separada delas. */
  perigo?: boolean
  desligado?: boolean
}

export function MenuDeAcoes({ rotulo = "Ações", acoes, desligado }: {
  rotulo?: string
  acoes: AcaoDoMenu[]
  desligado?: boolean
}) {
  return (
    <Menu.Root modal={false}>
      <Menu.Trigger
        disabled={desligado}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          border: "1px solid var(--line)", borderRadius: 6, padding: "4px 9px 4px 11px",
          fontSize: 12, background: "var(--surface)",
          color: desligado ? "var(--txt3)" : "var(--txt)", cursor: desligado ? "default" : "pointer",
        }}
      >
        {rotulo}
        <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"
             style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round", strokeLinejoin: "round" }}>
          <path d="m6 9.5 6 6 6-6" />
        </svg>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content
          align="end" sideOffset={4}
          onCloseAutoFocus={(e) => e.preventDefault()}
          style={{
            minWidth: 210, maxWidth: 300, padding: 5, borderRadius: 9,
            background: "var(--surface)", border: "1px solid var(--line2)",
            boxShadow: "var(--sombra)", color: "var(--txt)", zIndex: 50,
            ...escalaFlutuante,
          }}
        >
          {acoes.map((a, i) => (
            <div key={a.rotulo}>
              {a.perigo && i > 0 && !acoes[i - 1]!.perigo && (
                <Menu.Separator style={{ height: 1, background: "var(--line)", margin: "4px 0" }} />
              )}
              <Menu.Item
                disabled={a.desligado}
                onSelect={a.aoEscolher}
                className="menu-de-acoes-item"
                data-perigo={a.perigo || undefined}
                style={{
                  display: "flex", flexDirection: "column", gap: 1,
                  padding: "6px 9px", borderRadius: 6, outline: "none", fontSize: 12.5,
                  cursor: a.desligado ? "default" : "pointer",
                  color: a.desligado ? "var(--txt3)" : a.perigo ? "var(--erro)" : "var(--txt)",
                }}
              >
                {a.rotulo}
                {a.dica && <span style={{ fontSize: 10.5, color: "var(--txt3)", lineHeight: 1.35 }}>{a.dica}</span>}
              </Menu.Item>
            </div>
          ))}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  )
}
